import { Button } from "@gigpro/ui/Button";
import { DatePicker } from "@gigpro/ui/DatePicker";
import { Dialog, DialogFooter, DialogTitle } from "@gigpro/ui/Dialog";
import { Close } from "@gigpro/ui/Icons";
import { Stepper, StepperContent, StepperIndicator, StepperItem, StepperNav, StepperPanel } from "@gigpro/ui/Stepper";
import { useNow } from "@gigpro/ui/utils/useNow";
import { formatCurrency } from "@gigpro/utils";
import { formatDate } from "@gigpro/utils/datetime";
import type { HireFee, JobPostingResponse } from "@sidegigllc/api/client";
import { useJobCreditStats } from "api/queries/hire";
import { useProProfile } from "api/queries/pros";
import { env } from "config/env";
import { addDays } from "date-fns";
import { useJobsGatedAction } from "domains/hire/containers/JobsGateStep";
import { hireFeePricing } from "domains/hire/hireFeePlan";
import { useJobsGateState } from "domains/hire/hooks/useJobsGateState";
import { useSwitchToJobsPlan } from "domains/hire/hooks/useSwitchToJobsPlan";
import { payPerHireChoiceCopy, payPerHireSwitchFor } from "domains/hire/jobsPlan";
import React from "react";
import { toastApiError } from "utils/toastApiError";
import { PaymentSummaryContainer } from "../../containers/PaymentSummaryContainer";
import { JobCandidateCard, type JobCandidateCardData } from "../JobCandidateCard";
import { PayPerHireChoice, type PayPerHireOption } from "../PayPerHireChoice";

interface SendJobOfferDialogProps {
  open: boolean;
  onClose: () => void;
  candidate: JobCandidateCardData;
  job: JobPostingResponse;
  onSendOffer: (deadline: Date) => Promise<void>;
  isPending?: boolean;
  proId?: number;
  /** The applicant's fee when the caller has it; otherwise it is read from the pro profile. */
  hireFee?: HireFee | null;
}

export const SendJobOfferDialog: React.FC<SendJobOfferDialogProps> = ({
  open,
  onClose,
  candidate,
  job,
  onSendOffer,
  isPending = false,
  proId,
  hireFee,
}) => {
  const [acceptanceDeadline, setAcceptanceDeadline] = React.useState<Date | undefined>(() => addDays(new Date(), 3));
  const now = useNow({ updateIntervalMs: false });
  const [step, setStep] = React.useState<1 | 2>(1);
  const [showSuccess, setShowSuccess] = React.useState(false);

  const { data: proData, isLoading: isLoadingPro } = useProProfile(proId, { enabled: !!proId && !hireFee });
  const resolvedHireFee = hireFee ?? proData?.hire_fee_data ?? undefined;
  // Jobs plan pricing skips credits, so it never waits on them.
  const usesCredits = hireFeePricing(resolvedHireFee) === "legacy";
  const {
    data: creditStats,
    isLoading: isLoadingCredits,
    isError: isCreditsError,
  } = useJobCreditStats({ enabled: usesCredits });
  const isLoadingHireFee = !!proId && !hireFee && isLoadingPro;
  const { run: runGated, step: gateStep, ready: gateReady } = useJobsGatedAction(job.location);
  const { status: planStatus, canManage: canManagePlan } = useJobsGateState(job.location);
  const switchOption = payPerHireSwitchFor(planStatus);
  const [payOption, setPayOption] = React.useState<PayPerHireOption>("fee");
  const switchPlan = useSwitchToJobsPlan(job.location);
  // Held for the whole switch-then-send, since the plan refetch after the switch leaves a gap where nothing is pending
  const [isSwitching, setIsSwitching] = React.useState(false);
  const switching = !!switchOption && canManagePlan && payOption === "switch";
  // Don't send before the price the business is agreeing to has rendered, or the Jobs plan check can answer.
  const canProceed =
    gateReady && !isLoadingHireFee && (!usesCredits || (!isLoadingCredits && !isCreditsError && !!creditStats));

  const handleSendOffer = async () => {
    if (acceptanceDeadline && canProceed) {
      if (switching) {
        setIsSwitching(true);
        // All or nothing: a refused switch leaves the location on pay per hire and sends no offer
        try {
          await switchPlan.mutateAsync();
        } catch (error) {
          toastApiError(error);
          setIsSwitching(false);
          return;
        }
        try {
          await onSendOffer(acceptanceDeadline);
          setShowSuccess(true);
        } catch (error) {
          setIsSwitching(false);
          throw error;
        }
        setIsSwitching(false);
        return;
      }
      // A location without the Jobs plan sees the trial before the offer reaches a person
      await runGated(async () => {
        await onSendOffer(acceptanceDeadline);
        setShowSuccess(true);
      });
    }
  };

  const handleClose = () => {
    setStep(1);
    setShowSuccess(false);
    setPayOption("fee");
    onClose();
  };

  if (gateStep) {
    return (
      <Dialog bottomSheet open={open} onClose={handleClose} className="max-w-md">
        {gateStep}
      </Dialog>
    );
  }

  if (showSuccess) {
    return (
      <Dialog bottomSheet open={open} onClose={handleClose} className="max-w-md">
        <div className="flex items-center justify-between">
          <h2 className="text-extraLarge font-semibold">Job offer sent</h2>
          <button type="button" onClick={handleClose} className="text-brand">
            <Close className="size-5" />
          </button>
        </div>
        <p className="text-normal text-black">
          Your job offer has been sent to this pro. We'll notify you as soon as they respond. Track the status and view
          details of this offer from the tab "Job offered".
        </p>
        <DialogFooter>
          <Button variant="contained" className="w-full" onClick={handleClose}>
            Got it
          </Button>
        </DialogFooter>
      </Dialog>
    );
  }

  const feeSummary = (
    <PaymentSummaryContainer
      job={job}
      hireFee={resolvedHireFee}
      isLoadingHireFee={isLoadingHireFee}
      proFirstName={candidate.first_name}
    />
  );
  const locationName = job.location_details?.name ?? "";
  const positionName = job.name;
  const deadlineFormatted = acceptanceDeadline ? formatDate(acceptanceDeadline, "MMM d, yyyy") : "";

  return (
    <Dialog bottomSheet open={open} onClose={handleClose} className="max-w-xl" contentClassName="gap-0">
      <DialogTitle className="pb-0 text-extraLarge font-semibold">
        {step === 1 ? "Send job offer" : "Payment Summary"}
      </DialogTitle>

      <Stepper value={step}>
        <StepperNav>
          <StepperItem step={1}>
            <StepperIndicator className="h-1 w-full" />
          </StepperItem>
          <StepperItem step={2}>
            <StepperIndicator className="h-1 w-full" />
          </StepperItem>
        </StepperNav>

        <StepperPanel className="pt-1">
          <StepperContent value={1} className="flex flex-col gap-4">
            <p className="text-normal text-black">
              You can send offers to more than one pro for this job. Each pro who accepts is hired, and the job stays
              open until you close it. This offer stays open until the pro responds, the acceptance deadline passes, or
              you withdraw it.
            </p>
            <JobCandidateCard candidate={candidate} variant="horizontal" nonInteractive />
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1 text-normal">
                <span className="font-semibold text-black">Acceptance deadline</span>
                <span className="text-medium-gray">We recommend a 3 day (72 hour) window</span>
              </div>
              <DatePicker
                value={acceptanceDeadline}
                onValueChange={(date) => setAcceptanceDeadline(date)}
                minDate={now}
                compact
              />
            </div>

            <div className="flex flex-col gap-1 text-normal">
              <span className="font-semibold text-black">Offer Summary Preview</span>
              <span className="text-black">This offer will be sent in the app to the applicant</span>
            </div>

            <OfferPreview
              candidateName={candidate.first_name}
              positionName={positionName}
              locationName={locationName}
              wage={job.wage ?? 0}
              deadline={deadlineFormatted}
            />

            <DialogFooter className="gap-4">
              <Button variant="text" className="flex-1" onClick={handleClose} disabled={isPending}>
                Cancel
              </Button>
              <Button variant="contained" className="flex-1" onClick={() => setStep(2)} disabled={!acceptanceDeadline}>
                Next
              </Button>
            </DialogFooter>
          </StepperContent>

          <StepperContent value={2} className="flex flex-col gap-2">
            {usesCredits && (
              <p className="text-normal text-black">Review how credits and charges will apply to this job offer.</p>
            )}

            {switchOption ? (
              <PayPerHireChoice
                copy={payPerHireChoiceCopy(switchOption, job.location_details?.name ?? "this location")}
                canSwitch={canManagePlan}
                value={payOption}
                onValueChange={setPayOption}
                disabled={isPending || isSwitching}
                feeSummary={feeSummary}
              />
            ) : (
              feeSummary
            )}

            <div className="flex flex-col items-center gap-1 pt-2">
              <Button
                variant="contained"
                className="w-full"
                onClick={handleSendOffer}
                disabled={isPending || isSwitching || !canProceed}
                loading={isPending || isSwitching}
              >
                Confirm and send offer
              </Button>

              <Button variant="text" asChild>
                <a href={env.REACT_APP_MARKETPLACE_PLATFORM_TERMS_URL} target="_blank" rel="noopener noreferrer">
                  View Terms of Service
                </a>
              </Button>

              {usesCredits && (
                <p className="text-center text-normal text-black">
                  By posting this job you agree to be charged upon offer acceptance.
                </p>
              )}
            </div>
          </StepperContent>
        </StepperPanel>
      </Stepper>
    </Dialog>
  );
};

interface OfferPreviewProps {
  candidateName: string;
  positionName: string;
  locationName: string;
  wage: number;
  deadline: string;
}

const OfferPreview: React.FC<OfferPreviewProps> = ({ candidateName, positionName, locationName, wage, deadline }) => (
  <div className="rounded-sm border border-light-gray bg-background p-2 text-large text-black">
    <p>Hi {candidateName},</p>
    <br />
    <p>
      We're pleased to offer you the position of{" "}
      <strong>
        {positionName} at {locationName}
      </strong>
      {". "}
      We're excited about the skills and experience you bring and believe you'll be a valuable addition to our team.
    </p>
    <br />
    <p>
      <strong>Position:</strong> {positionName}
    </p>
    <p>
      <strong>Wage:</strong> {formatCurrency(wage)} per hour
    </p>
    <br />
    <p>
      Please note that this offer is contingent upon the successful completion of any required background checks and
      verification of your eligibility to work.
    </p>
    <br />
    <p>
      To accept this offer, please click <strong>"Accept offer"</strong> by <strong>{deadline}</strong>. If we do not
      receive your response by this date, we will assume you are no longer interested, and the offer may be extended to
      another candidate.
    </p>
    <br />
    <p>
      Once you've accepted, our hiring team will reach out with the next steps, including onboarding and orientation
      details.
    </p>
    <br />
    <p>We look forward to your response!</p>
    <br />
    <p>Best regards,</p>
    <p>{locationName}</p>
  </div>
);
